import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils.ts";
import type { Contact, Handle } from "../../types/contact.ts";
import { Button } from "./button.tsx";
import {
  ContactAutocompleteDropdown,
  type ContactAutocompleteDropdownRef,
  type ContactDropdownPosition,
} from "./contact-autocomplete.tsx";
import { Input } from "./input.tsx";

interface ContactInputProps {
  onSelectContact: (contact: Contact) => void;
  onSelectEmail?: (email: string) => void;
  placeholder?: string;
  buttonLabel?: string;
  buttonAriaLabel?: string;
  buttonClassName?: string;
  inputClassName?: string;
  filterHandleTypes?: Array<"emailAddress" | "phoneNumber" | "address">;
  onOpenChange?: (isOpen: boolean) => void;
  statsigName: string;
  /** Called when a specific handle is selected. Each contact is expanded into one row per matching handle. */
  onSelectHandle?: (contact: Contact, handle: Handle) => void;
}

/**
 * A button that opens an input field for searching and selecting contacts.
 * Used in email drafts and other forms where you need to add contacts.
 */
export const ContactInput = ({
  onSelectContact,
  onSelectEmail,
  placeholder = "Search people...",
  buttonLabel = "+",
  buttonAriaLabel = "Add contact",
  buttonClassName,
  inputClassName,
  filterHandleTypes = ["emailAddress"],
  onOpenChange,
  statsigName,
  onSelectHandle,
}: ContactInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<ContactAutocompleteDropdownRef>(null);
  const [dropdownPos, setDropdownPos] = useState<ContactDropdownPosition | null>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (dropdownPos || !isOpen || !inputRef.current || !query) return;

    const inputRect = inputRef.current.getBoundingClientRect();
    setDropdownPos({
      top: inputRect.bottom + window.scrollY,
      left: inputRect.left + window.scrollX,
      width: inputRect.width,
    });
  }, [dropdownPos, isOpen, query]);

  const closeInput = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setDropdownPos(null);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handleContactSelect = useCallback(
    (contact: Contact) => {
      onSelectContact(contact);
      closeInput();
    },
    [onSelectContact, closeInput],
  );

  const handleEmailSelect = useCallback(
    (email: string) => {
      if (onSelectEmail) {
        onSelectEmail(email);
        closeInput();
      }
    },
    [onSelectEmail, closeInput],
  );

  const handleHandleSelect = useCallback(
    (contact: Contact, handle: Handle) => {
      if (onSelectHandle) {
        onSelectHandle(contact, handle);
        closeInput();
      }
    },
    [onSelectHandle, closeInput],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        closeInput();
        return;
      }
      // The dropdown owns the highlight, so navigation and selection have to be
      // forwarded to it. Only mounted while there is a query, which is what
      // keeps Enter from being swallowed on an empty input.
      if (!dropdownRef.current) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        dropdownRef.current.handleArrowDown();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        dropdownRef.current.handleArrowUp();
      } else if (e.key === "Enter") {
        e.preventDefault();
        dropdownRef.current.handleEnter();
      }
    },
    [closeInput],
  );

  const handleOpenClick = useCallback(() => {
    setIsOpen(true);
    onOpenChange?.(true);
  }, [onOpenChange]);

  return (
    <div className="display-inline-block">
      {!isOpen ? (
        <Button
          onClick={handleOpenClick}
          variant="outlinePopover"
          size="action"
          className={cn(
            "h-6 text-xs px-2 py-1 font-medium transition-all duration-200 ease-in-out transform shrink-0",
            buttonClassName,
          )}
          aria-label={buttonAriaLabel}
          data-statsig-name={statsigName}
        >
          {buttonLabel}
        </Button>
      ) : (
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn(
            "h-6 px-3 py-2 border-none w-48 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent",
            inputClassName,
          )}
          autoFocus
          onBlur={closeInput}
        />
      )}
      {isOpen && query && dropdownPos && (
        <ContactAutocompleteDropdown
          ref={dropdownRef}
          query={query}
          position={dropdownPos}
          onSelect={handleContactSelect}
          onSelectEmail={handleEmailSelect}
          filterHandleTypes={filterHandleTypes}
          onSelectHandle={handleHandleSelect}
        />
      )}
    </div>
  );
};
